import { marketplace, money } from '@sellpy/commons'
import { Duration, DateTime } from 'luxon'
import { gql } from '@apollo/client'
import { AB_TESTS, inAbTestTestGroup } from '../../../common/analytics/abTest'

export const getHasOnlyOneFreightAlternative = (groupedFreightAlternatives) =>
  groupedFreightAlternatives?.every(({ freightAlternatives }) => {
    if (freightAlternatives.length !== 1) {
      return false
    }
    const options = Object.values(freightAlternatives[0].freightOptions)[0]?.options
    return !options || options.length === 1
  })

export const getFreightKey = ({ company, isExpressDelivery }) =>
  isExpressDelivery ? `${company}-express` : company

/** Gets the actual options stored in a `freightAlternative`, if it has any.
 * This function is named as such to draw attention to the fact that `freightAlternative.freightOptions` does not
 * actually store the freight options, but rather stores an object that stores an object that stores the freight options.
 *
 * @param {freightAlternative} freightAlternative
 * @returns {{id: string, name: string}[]?}
 */
export const getActualFreightOptions = (freightAlternative) =>
  freightAlternative && Object.values(freightAlternative.freightOptions)[0]?.options

export const getChosenFreightAlternativeAndOption = ({ orderGroup, freightChoices }) => {
  const { freightKey, optionIds, freightAlternative } = freightChoices?.[orderGroup] ?? {}
  const actualFreightOptions = getActualFreightOptions(freightAlternative)
  const chosenOptionId = optionIds?.[freightKey]
  const freightOption = actualFreightOptions?.find(({ id }) => id === chosenOptionId)
  return { freightAlternative, freightOption }
}

export const getEstimatedShipping = (freightAlternative, isFastHandlingSelected) =>
  (isFastHandlingSelected && freightAlternative.fastHandlingEstimatedShipping) ||
  freightAlternative.estimatedShipping
// Put cheapest alternative first and fastest alternative second.
// Also, all express delivery options should be shown directly below the chepaest option.
// Otherwise, the sorting is unchanged from the backend.
// This function also currently filters out the PostNord express option if in the fastHandling AB test.
export const getSortedFreightAlternatives = (alternativeGroup) => {
  // Note: we shouldn't sort by checking fast handling estimates because that would be annoying,
  // as the options might shift around when the user clicks a button.
  const deliveryTimeSortKey = (freightAlternative) => {
    const [earliest] = freightAlternative.estimatedShipping.interval.split('/')
    return Duration.fromISO(earliest).as('days')
  }

  const filteredAlternatives =
    alternativeGroup.isFastHandlingEligible && inAbTestTestGroup(AB_TESTS.fastHandling)
      ? alternativeGroup.freightAlternatives.filter(
          ({ company, isExpressDelivery }) => !(company === 'PostNordMyPack' && isExpressDelivery)
        )
      : [...alternativeGroup.freightAlternatives]

  const { fastest, cheapest } = getFastestAndCheapestAlternatives(filteredAlternatives)

  filteredAlternatives.sort((a, b) => {
    if (a === cheapest) {
      return -1
    }
    if (b === cheapest) {
      return 1
    }
    if (a === fastest) {
      return b === cheapest ? 1 : -1
    }
    if (b === fastest) {
      return a === cheapest ? -1 : 1
    }
    if (a.isExpressDelivery !== b.isExpressDelivery) {
      return -(Number(a.isExpressDelivery || false) - Number(b.isExpressDelivery || false))
    }
    if (a.isExpressDelivery) {
      return deliveryTimeSortKey(a) - deliveryTimeSortKey(b)
    }
    return 0
  })
  return filteredAlternatives
}

const KILL_TIME = {
  minute: 0,
  hour: 0,
  second: 0,
  millisecond: 0
}

export const getIntervalDatesForFreightAlternative = (freightAlternative) => {
  const now = DateTime.local()
  const [min, max] = freightAlternative.estimatedShipping.interval.split('/')
  return {
    earliest: now.plus(Duration.fromISO(min)).set(KILL_TIME),
    latest: now.plus(Duration.fromISO(max)).set(KILL_TIME)
  }
}
export const getFastestAndCheapestAlternatives = (freightAlternatives) => {
  if (freightAlternatives.length < 2) {
    return { cheapest: freightAlternatives[0], fastest: freightAlternatives[0] }
  }
  let cheapest = freightAlternatives[0],
    fastest = freightAlternatives[0]
  let cheapestPrice = freightAlternatives[0].price.amount,
    fastestDeliveryTime = getIntervalDatesForFreightAlternative(freightAlternatives[0])
  for (let i = 1; i < freightAlternatives.length; i++) {
    const price = freightAlternatives[i].price.amount
    const deliveryTime = getIntervalDatesForFreightAlternative(freightAlternatives[i])

    const isCheaper = price < cheapestPrice
    const isFaster =
      deliveryTime.latest < fastestDeliveryTime.latest ||
      (deliveryTime.latest === fastestDeliveryTime.latest &&
        deliveryTime.earliest < fastestDeliveryTime.earliest)

    if (isCheaper || (price === cheapestPrice && isFaster)) {
      cheapestPrice = price
      cheapest = freightAlternatives[i]
    }
    if (isFaster || (deliveryTime.latest === fastestDeliveryTime.latest && isCheaper)) {
      fastestDeliveryTime = deliveryTime
      fastest = freightAlternatives[i]
    }
  }
  return { cheapest, fastest }
}

export const FREIGHT_FROM_LAST_ORDER = gql`
  query latestPayment {
    # Disallow p2p orders because they only have one freight option
    orders(order: createdAt_DESC, first: 1, where: { p2p: { equalTo: false } }) {
      edges {
        node {
          freightFromLastOrder: freight
        }
      }
    }
  }
`

const getLastFreightChoice = (groupedFreightAlternatives, freightFromLastOrder) => {
  if (!freightFromLastOrder || !inAbTestTestGroup(AB_TESTS.oneStepCheckout)) {
    return false
  }
  const lastFreightKey = getFreightKey(freightFromLastOrder)

  const lastFreightAlternative = groupedFreightAlternatives
    .find(({ orderGroup }) => orderGroup === 'regular')
    ?.freightAlternatives?.find((alt) => getFreightKey(alt) === lastFreightKey)
  if (!lastFreightAlternative) {
    return false
  }
  const optionsKey = Object.keys(lastFreightAlternative.freightOptions)[0]
  const lastOptionId = freightFromLastOrder[optionsKey]

  const actualFreightOptions = getActualFreightOptions(lastFreightAlternative)

  if (
    (optionsKey && !lastOptionId) ||
    (lastOptionId && !actualFreightOptions?.find(({ id }) => id === lastOptionId)) ||
    (!lastOptionId && actualFreightOptions?.length)
  ) {
    return false
  }

  return {
    freightAlternative: lastFreightAlternative,
    freightKey: lastFreightKey,
    optionId: lastOptionId
  }
}

/** Tries to preselect the `freightChoices` if possible.
 * If there is only one freight choice, that will be preselected
 * Otherwise, if the user is in the fast handling AB test, their last chosen freight alternative
 * will be preselected.
 */
export const getFreightPreselectionIfPossible = ({
  groupedFreightAlternatives,
  freightFromLastOrder
}) => {
  if (!groupedFreightAlternatives) {
    return
  }

  const hasOnlyOneFreightAlternative = getHasOnlyOneFreightAlternative(groupedFreightAlternatives)
  const lastFreightChoice = getLastFreightChoice(groupedFreightAlternatives, freightFromLastOrder)

  if (!hasOnlyOneFreightAlternative && !lastFreightChoice) {
    return
  }

  const freightChoices = {}
  for (const freightAlternativeGroup of groupedFreightAlternatives) {
    if (freightAlternativeGroup.orderGroup === 'regular' && lastFreightChoice) {
      const { freightKey, optionId, freightAlternative } = lastFreightChoice

      freightChoices[freightAlternativeGroup.orderGroup] = {
        freightKey,
        freightAlternative,
        optionIds: { [freightKey]: optionId }
      }
    } else {
      const freightAlternative = freightAlternativeGroup.freightAlternatives[0]
      const freightKey = getFreightKey(freightAlternative)
      const onlyOption = Object.values(freightAlternative.freightOptions)[0]?.options?.[0]
      freightChoices[freightAlternativeGroup.orderGroup] = {
        freightKey,
        freightAlternative,
        optionIds: { [freightKey]: onlyOption?.id }
      }
    }
  }
  return freightChoices
}

export const calculateTotalFreightCost = ({
  freightChoices,
  isFastHandlingSelected,
  fastHandlingPrice
}) => {
  const totals = {
    price: { amount: 0, currency: marketplace.CURRENCY[process.env.REGION] },
    originalPrice: { amount: 0, currency: marketplace.CURRENCY[process.env.REGION] }
  }
  if (!freightChoices) {
    return totals
  }

  if (isFastHandlingSelected && !freightChoices.regular?.freightAlternative.isExpressDelivery) {
    totals.price = money.add(totals.price, fastHandlingPrice)
  }
  for (const orderGroup in freightChoices ?? {}) {
    const { price, originalPrice } = freightChoices[orderGroup].freightAlternative
    if (price) {
      totals.price = money.add(totals.price, price)
    }
    if (originalPrice) {
      totals.originalPrice = money.add(totals.originalPrice, originalPrice)
    }
  }
  return totals
}
